import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import FormInput from "../components/FormInput";
import PasswordFormField from "../components/PasswordFormField";
import FormSelect from "../components/FormSelect";
import PrimaryButton from "../components/PrimaryButton";
import { getCreateTanneryUserErrorMessage } from "../utils/createTanneryUserApiErrors";

const AUTH_TOKEN_STORAGE_KEY = "authToken";
const AUTH_ROLE_STORAGE_KEY = "authRole";
const normalizeRole = (role) => {
  const cleaned = String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, "_");

  if (cleaned === "TANNERYADMIN") {
    return "TANNERY_ADMIN";
  }

  return cleaned;
};
const USER_ROLE_OPTIONS = ["DataEntry", "TokenGenerator"];
const INITIAL_USER_FORM = {
  role: "",
  name: "",
  username: "",
  password: "",
};
const MANAGEABLE_ROLES = new Set(["DATAENTRY", "TOKENGENERATOR"]);

const getBaseApiUrl = () => {
  const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
  if (!baseApiUrl) {
    throw new Error("REACT_APP_BASE_API_URL is not configured.");
  }
  return baseApiUrl.replace(/\/+$/, "");
};

function CreateUsersPage() {
  const navigate = useNavigate();
  const isTanneryAdmin =
    normalizeRole(localStorage.getItem(AUTH_ROLE_STORAGE_KEY)) ===
    "TANNERY_ADMIN";
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [userForm, setUserForm] = useState(INITIAL_USER_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [createMessage, setCreateMessage] = useState("");
  const [createError, setCreateError] = useState("");
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [togglingUserId, setTogglingUserId] = useState("");

  const normalizeUserRole = (role) =>
    String(role || "")
      .trim()
      .toUpperCase()
      .replace(/[\s-]/g, "");

  const getUserRoleLabel = (role) => {
    if (normalizeUserRole(role) === "DATAENTRY") {
      return "DataEntry";
    }
    if (normalizeUserRole(role) === "TOKENGENERATOR") {
      return "TokenGenerator";
    }
    return role || "Unknown";
  };

  const parseApiMessage = async (response, fallbackMessage) => {
    const responseText = await response.text();
    if (!responseText) {
      return fallbackMessage;
    }

    try {
      const payload = JSON.parse(responseText);
      return payload?.message || payload?.detail || fallbackMessage;
    } catch (parseError) {
      return responseText;
    }
  };

  const fetchManagedUsers = useCallback(
    async ({ preserveError = false } = {}) => {
      const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
      if (!token) {
        navigate("/", { replace: true });
        return;
      }

      try {
        setUsersLoading(true);
        if (!preserveError) {
          setUsersError("");
        }
        const response = await fetch(`${getBaseApiUrl()}/tannery-admin/users`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(await parseApiMessage(response, "Unable to load users."));
        }

        const payload = await response.json();
        const userList = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : [];
        const normalizedUsers = userList
          .filter((user) => MANAGEABLE_ROLES.has(normalizeUserRole(user.role)))
          .map((user) => ({
            user_id: user.user_id || user.id || "",
            name: user.name || "",
            username: user.username || "",
            role: user.role || "",
            is_active: Boolean(user.is_active),
          }));
        setUsers(normalizedUsers);
      } catch (error) {
        setUsersError(error.message || "Unable to load users.");
      } finally {
        setUsersLoading(false);
      }
    },
    [navigate]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setUserForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    setCreateError("");
    setCreateMessage("");
  };

  const validateUserForm = () => {
    const errors = {};

    if (!userForm.role.trim()) {
      errors.role = "Please select a role.";
    }
    if (!userForm.name.trim()) {
      errors.name = "Name is required.";
    }
    if (!userForm.username.trim()) {
      errors.username = "Username is required.";
    }
    if (!userForm.password.trim()) {
      errors.password = "Password is required.";
    }

    return errors;
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    const validationErrors = validateUserForm();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      setCreateError("Session expired. Please login again.");
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsCreatingUser(true);
      setCreateError("");
      setCreateMessage("");

      const response = await fetch(`${getBaseApiUrl()}/tannery-admin/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          role: userForm.role,
          name: userForm.name.trim(),
          username: userForm.username.trim(),
          password: userForm.password,
        }),
      });

      const responseText = await response.text();
      let responsePayload = null;
      let nonJsonBody = "";

      if (responseText) {
        try {
          responsePayload = JSON.parse(responseText);
        } catch (parseError) {
          nonJsonBody = responseText;
        }
      }

      if (!response.ok) {
        const apiHint =
          (responsePayload &&
            typeof responsePayload.message === "string" &&
            responsePayload.message.trim()) ||
          (responsePayload && typeof responsePayload.detail === "string" && responsePayload.detail.trim()) ||
          (nonJsonBody && nonJsonBody.trim()) ||
          undefined;
        throw new Error(
          getCreateTanneryUserErrorMessage(response.status, responsePayload, apiHint)
        );
      }

      let message = "User created successfully.";
      if (
        responsePayload &&
        typeof responsePayload.message === "string" &&
        responsePayload.message.trim()
      ) {
        message = responsePayload.message.trim();
      }

      if (responsePayload?.name && responsePayload?.username && responsePayload?.role) {
        message = `User "${responsePayload.name}" (${responsePayload.username}) created as ${responsePayload.role}.`;
      } else if (responsePayload?.username && responsePayload?.role) {
        message = `User "${responsePayload.username}" created as ${responsePayload.role}.`;
      }

      setCreateMessage(message);
      setUserForm(INITIAL_USER_FORM);
      setFieldErrors({});
      await fetchManagedUsers({ preserveError: true });
    } catch (error) {
      setCreateError(error.message || "Unable to create user right now.");
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleToggleRestriction = async (user) => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      setUsersError("Session expired. Please login again.");
      navigate("/", { replace: true });
      return;
    }

    try {
      setUsersError("");
      setCreateMessage("");
      setCreateError("");
      setTogglingUserId(String(user.user_id));

      const response = await fetch(
        `${getBaseApiUrl()}/tannery-admin/users/${user.user_id}/restriction`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            is_active: !user.is_active,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          await parseApiMessage(
            response,
            "Unable to update user restriction status."
          )
        );
      }

      setUsers((prevUsers) =>
        prevUsers.map((existingUser) =>
          existingUser.user_id === user.user_id
            ? {
                ...existingUser,
                is_active: !user.is_active,
              }
            : existingUser
        )
      );
    } catch (error) {
      setUsersError(error.message || "Unable to update user restriction status.");
    } finally {
      setTogglingUserId("");
    }
  };

  useEffect(() => {
    if (!isTanneryAdmin) {
      return;
    }

    fetchManagedUsers();
  }, [fetchManagedUsers, isTanneryAdmin]);

  if (!isTanneryAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <section className="dashboard-card" id="create-users">
      <h2>Create User</h2>
      <p className="dashboard-card-subtitle">
        Tannery admin can create one DataEntry and one TokenGenerator user.
      </p>

      {createMessage ? (
        <p className="dashboard-success-message" role="status">
          {createMessage}
        </p>
      ) : null}
      {createError ? (
        <p className="dashboard-error-message" role="alert">
          {createError}
        </p>
      ) : null}

      <form onSubmit={handleCreateUser} noValidate>
        <FormSelect
          id="role"
          name="role"
          label="Role"
          value={userForm.role}
          onChange={handleChange}
          options={USER_ROLE_OPTIONS}
          placeholder="Select role"
          error={fieldErrors.role}
          required
        />
        <FormInput
          id="name"
          name="name"
          label="Name"
          value={userForm.name}
          onChange={handleChange}
          placeholder="Enter full name"
          error={fieldErrors.name}
          required
        />
        <FormInput
          id="username"
          name="username"
          label="Username"
          value={userForm.username}
          onChange={handleChange}
          placeholder="Enter username"
          error={fieldErrors.username}
          required
        />
        <PasswordFormField
          id="password"
          name="password"
          label="Password"
          value={userForm.password}
          onChange={handleChange}
          placeholder="Enter password"
          error={fieldErrors.password}
          required
        />

        <PrimaryButton type="submit">
          {isCreatingUser ? "Creating..." : "Create User"}
        </PrimaryButton>
      </form>

      <section className="dashboard-users-list" aria-label="Managed users">
        <div className="dashboard-users-list-header">
          <h3>Created Users</h3>
          <PrimaryButton onClick={() => fetchManagedUsers()}>
            {usersLoading ? "Refreshing..." : "Refresh"}
          </PrimaryButton>
        </div>
        <p className="dashboard-card-subtitle">
          Restrict or unrestrict DataEntry and TokenGenerator users.
        </p>

        {usersError ? (
          <p className="dashboard-error-message" role="alert">
            {usersError}
          </p>
        ) : null}

        {usersLoading ? <p>Loading users...</p> : null}

        {!usersLoading && users.length === 0 ? (
          <p>No users found for this tannery yet.</p>
        ) : null}

        {!usersLoading && users.length > 0 ? (
          <div className="dashboard-users-table-wrapper">
            <table className="dashboard-users-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Username</th>
                  <th scope="col">Role</th>
                  <th scope="col">Status</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.user_id}>
                    <td>{user.name || "-"}</td>
                    <td>{user.username}</td>
                    <td>{getUserRoleLabel(user.role)}</td>
                    <td>
                      <span
                        className={`user-status-badge ${
                          user.is_active
                            ? "user-status-badge-active"
                            : "user-status-badge-restricted"
                        }`}
                      >
                        {user.is_active ? "Active" : "Restricted"}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className={`restriction-toggle-btn ${
                          user.is_active
                            ? "restriction-toggle-btn-restrict"
                            : "restriction-toggle-btn-unrestrict"
                        }`}
                        onClick={() => handleToggleRestriction(user)}
                        disabled={togglingUserId === String(user.user_id)}
                      >
                        {togglingUserId === String(user.user_id)
                          ? "Updating..."
                          : user.is_active
                          ? "Restrict"
                          : "Unrestrict"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </section>
  );
}

export default CreateUsersPage;
